import {
  ChatMessage,
  ChatMessageWithParts,
  ContentType,
  ImageContent,
} from "src/views/assistant/types";
import {
  ConversationRequest,
  EquipmentAIChatContext,
  ProductViewContext,
  ResponseMode,
  SearchFilterPredicate,
  TextGenModelParams,
} from "../types";

/**
 * Dataset with optional search filters for RAG search.
 * When filters are provided, the backend will filter records during search.
 */
interface Dataset {
  id: number;
  name?: string;
  filters?: SearchFilterPredicate[];
  [key: string]: unknown;
}

interface CreateRequestPayloadParams {
  api: string;
  /** Model ID. If null/undefined, backend will use GeneralLLM from resource slot bindings */
  modelId?: number | null;
  /** Model version number. If null/undefined, backend will resolve from resource slot bindings */
  modelVersionNum?: number | null;
  dialogue: ChatMessageWithParts[];
  datasetsForSearchContext: Dataset[];
  /** Source data record IDs to limit search to specific documents */
  sourceDataRecordIds?: number[];
  numSearchResults?: number;
  dialogueId?: number;
  textGenModelParams: TextGenModelParams;
  systemPrompt?: string | null;
  isOP?: boolean;
  activityId: number | null;
  chatConfigId?: number | null;
  /** Images to include with the latest user message (for VLM support) */
  images?: ImageContent[];
  product?: string | null;
  /** Product-specific context for specialized chat features (e.g., ER sensor plots) */
  productContext?: EquipmentAIChatContext;
  /** Snapshot of the chart-panel view at conversation start. */
  viewContext?: ProductViewContext | null;
  /** Answer style for the conversation; omitted from the body when unset. */
  responseMode?: ResponseMode;
}

/**
 * Strip `<tool_update>...</tool_update>` preview blocks from tool_call text.
 * These are early-detection previews injected during streaming and must be
 * removed before the backend parses the JSON tool call data.
 */
function stripToolUpdateTags(text: string): string {
  return text.replace(/<tool_update>[\s\S]*?<\/tool_update>/g, "");
}

function mergeContentParts(
  message: ChatMessageWithParts,
  images?: ImageContent[],
): ChatMessage {
  const combinedText = message.contentParts.map((part) => part.text).join("");

  const { contentParts, images: existingImages, ...rest } = message;

  // Determine which images to include:
  // - For new messages: use the provided images parameter
  // - For historical messages: preserve existing images from dialogue history
  const messageImages =
    images && images.length > 0
      ? images
      : existingImages && existingImages.length > 0
        ? existingImages
        : undefined;

  // For assistant messages with tool_call/tool_response parts, include
  // structured content_parts so the backend can reconstruct the proper
  // OpenAI message format (structured tool_calls + tool role messages)
  // instead of treating the entire flattened text as a single message.
  const hasToolContent =
    message.type !== "User" &&
    message.type !== "System" &&
    contentParts.some(
      (p) =>
        p.type === ContentType.TOOL_CALL ||
        p.type === ContentType.TOOL_RESPONSE,
    );

  const serializedContentParts = hasToolContent
    ? contentParts.map((p) => ({
        type: p.type,
        text:
          p.type === ContentType.TOOL_CALL
            ? stripToolUpdateTags(p.text)
            : p.text,
      }))
    : undefined;

  return {
    ...rest,
    content: {
      text: combinedText,
      // Only attach images to user messages (both new and historical)
      ...(messageImages && message.type === "User"
        ? { images: messageImages }
        : {}),
      ...(serializedContentParts
        ? { content_parts: serializedContentParts }
        : {}),
    },
  };
}

export function convertDialogueToSingleContent(
  dialogue: ChatMessageWithParts[],
  images?: ImageContent[],
): ChatMessage[] {
  // Attach images only to the last user message
  return dialogue.map((message, index) => {
    const isLastUserMessage =
      message.type === "User" &&
      !dialogue.slice(index + 1).some((m) => m.type === "User");
    return mergeContentParts(message, isLastUserMessage ? images : undefined);
  });
}

function syncSystemPromptInDialogue(
  dialogue: ChatMessage[],
  systemPrompt?: string | null,
  isOP?: boolean,
): ChatMessage[] {
  const systemPromptInDialogue = dialogue.find(
    (message) => message.type === "System",
  );

  // Add system prompt if it exists but is not in dialogue
  if (systemPrompt && !systemPromptInDialogue) {
    const systemMessage: ChatMessage = {
      type: "System",
      feedback_value: null,
      content: { text: systemPrompt },
    };
    return [systemMessage, ...dialogue];
  }

  // Update existing system prompt if it's different
  if (
    systemPrompt &&
    systemPromptInDialogue &&
    systemPromptInDialogue.content.text !== systemPrompt
  ) {
    return dialogue.map((message) =>
      message.type === "System"
        ? { ...message, content: { text: systemPrompt } }
        : message,
    );
  }

  // Remove system prompt if it shouldn't be there (not OP mode and no system prompt provided)
  if (!systemPrompt && systemPromptInDialogue && !isOP) {
    return dialogue.filter((message) => message.type !== "System");
  }

  return dialogue;
}

/**
 * Creates the model inference data for the conversation request
 */
function createModelInferenceData(
  dialogue: ChatMessageWithParts[],
  systemPrompt: string | null | undefined,
  textGenModelParams: TextGenModelParams,
  isOP: boolean | undefined,
  images?: ImageContent[],
) {
  const finalTextGenModelParams: TextGenModelParams = {
    ...textGenModelParams,
  };

  const singleContentDialogue = convertDialogueToSingleContent(
    dialogue,
    images,
  );
  const processedDialogue = syncSystemPromptInDialogue(
    singleContentDialogue,
    systemPrompt,
    isOP,
  );

  // Backend's apply_templates rejects messages with type=None ("Unknown
  // message type"). A streaming chunk that arrives without a `type` field
  // gets stored in the dialogue with type=undefined and then sent back on
  // the next /chat/start, killing the turn. Default any missing type to
  // "Assistant" — these can only originate from server-streamed events,
  // which are always assistant-side.
  const sanitizedDialogue = processedDialogue.map((msg) => {
    if (!msg.type) {
      console.warn(
        "[createRequestPayload] dialogue message missing type, defaulting to Assistant:",
        msg,
      );
      return { ...msg, type: "Assistant" as const };
    }
    return msg;
  });

  return {
    infer_data: JSON.stringify(sanitizedDialogue),
    text_gen_model_params: finalTextGenModelParams,
  };
}

function createSearchConfig(
  datasetsForSearchContext: Dataset[],
  sourceDataRecordIds?: number[],
  numSearchResults?: number,
) {
  const datasetIds =
    datasetsForSearchContext?.map((dataset) => dataset.id) ?? [];
  const hasDatasets = datasetIds.length > 0;
  const hasSdrIds = sourceDataRecordIds && sourceDataRecordIds.length > 0;

  if (!hasDatasets && !hasSdrIds) {
    return {
      search_enhanced: false,
      search_datasets: [],
    };
  }

  // Check if any datasets have filters
  const hasFilters = datasetsForSearchContext?.some(
    (d) => d.filters && d.filters.length > 0,
  );

  // If any dataset has filters, use the new per-dataset filter format
  if (hasFilters) {
    const datasetsWithFilters = datasetsForSearchContext.map((dataset) => ({
      dataset_id: dataset.id,
      filters: dataset.filters || [],
    }));

    return {
      search_enhanced: true,
      search_datasets_with_filters: datasetsWithFilters,
      ...(hasSdrIds && { source_data_record_ids: sourceDataRecordIds }),
      n_search_results: numSearchResults,
    };
  }

  // Legacy format: just dataset IDs without filters
  return {
    search_enhanced: false,
    search_datasets: datasetIds,
    ...(hasSdrIds && { source_data_record_ids: sourceDataRecordIds }),
    n_search_results: numSearchResults,
  };
}

export const createRequestPayload = ({
  api,
  modelId,
  modelVersionNum,
  dialogue,
  datasetsForSearchContext,
  sourceDataRecordIds,
  numSearchResults,
  dialogueId,
  textGenModelParams,
  systemPrompt,
  isOP,
  activityId,
  chatConfigId,
  images,
  product,
  productContext,
  viewContext,
  responseMode,
}: CreateRequestPayloadParams): ConversationRequest => {
  if (!api) {
    throw new Error("API endpoint is required to create request payload");
  }

  // Note: modelId and modelVersionNum can be null - backend will use GeneralLLM from resource slot bindings
  const modelInferenceData = createModelInferenceData(
    dialogue,
    systemPrompt,
    textGenModelParams,
    isOP,
    images,
  );

  const searchConfig = createSearchConfig(
    datasetsForSearchContext,
    sourceDataRecordIds,
    numSearchResults,
  );

  const conversationRequest: ConversationRequest = {
    stream: true,
    model_id: modelId ?? null,
    model_version_number: modelVersionNum ?? null,
    model_inference_data: modelInferenceData,
    dialogue_id: dialogueId,
    tool_calling: true,
    activity_id: activityId,
    chat_config_id: chatConfigId,
    product: product,
    ...searchConfig,
    ...(productContext ? { product_context: productContext } : {}),
    ...(viewContext ? { view_context: viewContext } : {}),
    ...(responseMode ? { response_mode: responseMode } : {}),
  };

  return conversationRequest;
};
